import React from 'react'
import { LocalStorageManager, SessionStorageManager } from '../helper/localStorage'
import { getUrlParamValue, smartParseParam } from '../helper/params'
import { normalizePaymentMethodCode } from '../helper/urlHelper'
import {
  PARAM_IFRAME_HEADER_TITLE,
  PARAM_HIDE_PAYMENT_METHOD,
  PARAM_IFRAME_PAYMENT,
  PARAM_IS_HEADER_MINI_APP,
  PARAM_IS_WEB_VIEW,
  PARAM_PAYMENT_METHOD,
  PARAM_PAYMENT_THEME_NAME,
  PARAM_PAYMENT_STEP_BAR_TYPE,
  PARAM_RETURN_URL,
  PARAM_REFER_STATION_ID,
  PARAM_REFER_USER_ID
} from '../constants/params'
import addKeyLocalStorage from '../helper/localStorage'
export const AppParamsContext = React.createContext(null)

const getFirstUrlParamValue = (paramKey, search) => {
  if (Array.isArray(paramKey)) {
    for (const key of paramKey) {
      const value = getUrlParamValue(key, search)
      if (value !== null && value !== undefined) return value
    }
    return null
  }

  return getUrlParamValue(paramKey, search)
}

const parseBooleanParam = (value) => {
  if (value === true || value === 1) return true
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    return normalized === '1' || normalized === 'true'
  }
  return false
}

const resolveParamsMiniAppSessionStorage = (config) => {
  // 1. param (raw)
  const param = getFirstUrlParamValue(config.paramKey)

  // có param thì lưu session (raw)
  if (param !== null && param !== undefined && config.storageKey) {
    SessionStorageManager.setItem(config.storageKey, param)
  }

  // 2. resolve raw theo thứ tự ưu tiên
  const raw = param ?? SessionStorageManager.getItem(config.storageKey) ?? smartParseParam(process.env?.[config.envKey]) ?? config.defaultValue

  // 3. parse giá trị cuối cùng
  return config.parser ? config.parser(raw) : raw
}

const resolveParamsMiniAppLocalStorage = (config) => {
  // 1. param (raw)
  const param = getFirstUrlParamValue(config.paramKey)

  // có param thì lưu local (raw)
  if (param !== null && param !== undefined && config.storageKey) {
    LocalStorageManager.setItem(config.storageKey, param)
  }

  // 2. resolve raw theo thứ tự ưu tiên
  const raw = param ?? LocalStorageManager.getItem(config.storageKey) ?? smartParseParam(process.env?.[config.envKey]) ?? config.defaultValue

  // 3. parse giá trị cuối cùng
  return config.parser ? config.parser(raw) : raw
}

/* =====================
 * PARAM MINI APP SCHEMA
 * ===================== */
const PARAMS_MINIAPP_SCHEMA_SESSION_STORAGE = {
  isWebView: {
    paramKey: PARAM_IS_WEB_VIEW,
    storageKey: PARAM_IS_WEB_VIEW,
    envKey: undefined,
    defaultValue: false,
    parser: parseBooleanParam
  },
  isHeaderMiniApp: {
    paramKey: PARAM_IS_HEADER_MINI_APP,
    storageKey: PARAM_IS_HEADER_MINI_APP,
    envKey: undefined,
    defaultValue: false,
    parser: parseBooleanParam
  },
  referUserId: {
    paramKey: PARAM_REFER_USER_ID,
    storageKey: PARAM_REFER_USER_ID,
    envKey: undefined,
    defaultValue: undefined,
    parser: (v) => v
  },
  referStationId: {
    paramKey: PARAM_REFER_STATION_ID,
    storageKey: PARAM_REFER_STATION_ID,
    envKey: undefined,
    defaultValue: undefined,
    parser: (v) => v
  },
  apiKey: {
    paramKey: ['apiKey', 'apikey'],
    storageKey: 'apiKey',
    envKey: 'REACT_APP_APIKEY',
    defaultValue: undefined,
    parser: (v) => {
      if (v !== undefined && v !== null && v !== '') return String(v)
      const fallback = process.env.REACT_APP_API_KEY
      return fallback ? String(fallback) : undefined
    }
  },
  mode: {
    paramKey: ['mode'],
    storageKey: 'mode',
    envKey: undefined,
    defaultValue: undefined,
    parser: (raw) => {
      // Ưu tiên query/storage
      const val = String(raw || '').toLowerCase()
      if (val === 'gtel' || val === 'gtelpay') return 'gtel'
      if (val === 'zalopay' || val === 'zalo') return 'zalopay'

      // fallback env OR: REACT_APP_MINIAPP_GTELPAY=1 hoặc REACT_APP_MINIAPP_ZALOPAY=1
      if (String(process.env.REACT_APP_MINIAPP_GTELPAY) === '1') return 'gtel'
      if (String(process.env.REACT_APP_MINIAPP_ZALOPAY) === '1') return 'zalopay'
      return undefined
    }
  },
  themeName: {
    paramKey: ['themeName', 'theme'],
    storageKey: 'themeName',
    envKey: 'REACT_APP_THEME_NAME',
    defaultValue: undefined,
    parser: (v) => (v === undefined || v === null ? undefined : String(v))
  },
  headerTitle: {
    paramKey: [PARAM_IFRAME_HEADER_TITLE, 'headerTitle'],
    storageKey: PARAM_IFRAME_HEADER_TITLE,
    envKey: undefined,
    defaultValue: undefined,
    parser: (v) => (v === undefined || v === null || v === '' ? undefined : String(v))
  },
  paymentThemeName: {
    paramKey: [PARAM_PAYMENT_THEME_NAME],
    storageKey: PARAM_PAYMENT_THEME_NAME,
    envKey: 'REACT_APP_PAYMENT_THEME_NAME',
    defaultValue: undefined,
    parser: (v) => (v === undefined || v === null || v === '' ? undefined : String(v))
  },
  paymentMethodCode: {
    paramKey: PARAM_PAYMENT_METHOD,
    storageKey: PARAM_PAYMENT_METHOD,
    envKey: undefined,
    defaultValue: undefined,
    parser: (v) => normalizePaymentMethodCode(v)
  },
  hidePaymentMethod: {
    paramKey: [PARAM_HIDE_PAYMENT_METHOD],
    storageKey: PARAM_HIDE_PAYMENT_METHOD,
    envKey: undefined,
    defaultValue: false,
    parser: parseBooleanParam
  },
  isIframePayment: {
    paramKey: [PARAM_IFRAME_PAYMENT, 'iframePayment'],
    storageKey: PARAM_IFRAME_PAYMENT,
    envKey: undefined,
    defaultValue: false,
    parser: parseBooleanParam
  },
  paymentStepBarType: {
    paramKey: [PARAM_PAYMENT_STEP_BAR_TYPE],
    storageKey: PARAM_PAYMENT_STEP_BAR_TYPE,
    envKey: undefined,
    defaultValue: undefined,
    parser: (v) => {
      const normalized = String(v || '').trim().toLowerCase()
      if (normalized === 'trungtam') return 'trungtam'
      if (normalized === 'baohiem') return 'baohiem'
      return undefined
    }
  },
  isEmbeddedView: {
    paramKey: ['isEmbeddedView', 'isembbedview'],
    storageKey: 'isEmbeddedView',
    envKey: undefined,
    defaultValue: false,
    parser: parseBooleanParam
  },
  returnUrl: {
    paramKey: [PARAM_RETURN_URL, 'returnUrl'],
    storageKey: PARAM_RETURN_URL,
    envKey: undefined,
    defaultValue: '',
    parser: (v) => (v === undefined || v === null ? '' : String(v))
  }
  // authFlow removed per design
}

const PARAMS_MINIAPP_SCHEMA_LOCAL_STORAGE = {}

const matchesParamKey = (config, paramKey) => {
  if (!config || !paramKey) return false
  if (Array.isArray(config.paramKey)) {
    return config.paramKey.includes(paramKey)
  }
  return config.paramKey === paramKey
}

const findMiniAppParamConfig = (paramKey) => {
  if (!paramKey) return null

  if (PARAMS_MINIAPP_SCHEMA_SESSION_STORAGE[paramKey]) {
    return {
      contextKey: paramKey,
      config: PARAMS_MINIAPP_SCHEMA_SESSION_STORAGE[paramKey],
      storage: 'session'
    }
  }

  if (PARAMS_MINIAPP_SCHEMA_LOCAL_STORAGE[paramKey]) {
    return {
      contextKey: paramKey,
      config: PARAMS_MINIAPP_SCHEMA_LOCAL_STORAGE[paramKey],
      storage: 'local'
    }
  }

  const sessionEntry = Object.entries(PARAMS_MINIAPP_SCHEMA_SESSION_STORAGE).find(
    ([, config]) => matchesParamKey(config, paramKey) || config.storageKey === paramKey
  )

  if (sessionEntry) {
    return {
      contextKey: sessionEntry[0],
      config: sessionEntry[1],
      storage: 'session'
    }
  }

  const localEntry = Object.entries(PARAMS_MINIAPP_SCHEMA_LOCAL_STORAGE).find(
    ([, config]) => matchesParamKey(config, paramKey) || config.storageKey === paramKey
  )

  if (localEntry) {
    return {
      contextKey: localEntry[0],
      config: localEntry[1],
      storage: 'local'
    }
  }

  return null
}

const buildInitialParamsValue = () => {
  const params = {}

  Object.keys(PARAMS_MINIAPP_SCHEMA_SESSION_STORAGE).forEach((key) => {
    params[key] = resolveParamsMiniAppSessionStorage(PARAMS_MINIAPP_SCHEMA_SESSION_STORAGE[key])
  })

  Object.keys(PARAMS_MINIAPP_SCHEMA_LOCAL_STORAGE).forEach((key) => {
    params[key] = resolveParamsMiniAppLocalStorage(PARAMS_MINIAPP_SCHEMA_LOCAL_STORAGE[key])
  })

  return params
}

export const AppParamsContextProvider = (props) => {
  const [paramsValue, setParamsValue] = React.useState(() => buildInitialParamsValue())

  const checkUrlParamSaveContext = React.useCallback((paramKey, options = {}) => {
    if (!paramKey) return undefined

    const schema = findMiniAppParamConfig(paramKey)
    const config = schema?.config ?? { paramKey, storageKey: paramKey, parser: undefined }
    const contextKey = schema?.contextKey ?? paramKey

    const paramValue = getFirstUrlParamValue(config.paramKey, options.search)
    if (paramValue === null || paramValue === undefined) return undefined

    const parsedValue = config.parser ? config.parser(paramValue) : paramValue
    const storageMode = options.storage ?? 'auto'
    const storageType = storageMode === 'auto' ? schema?.storage : storageMode

    if (storageType === 'session' && config.storageKey) {
      SessionStorageManager.setItem(config.storageKey, paramValue)
    }

    if (storageType === 'local' && config.storageKey) {
      LocalStorageManager.setItem(config.storageKey, paramValue)
    }

    setParamsValue((prev) => {
      if (prev?.[contextKey] === parsedValue) return prev
      return {
        ...prev,
        [contextKey]: parsedValue
      }
    })

    return parsedValue
  }, [])

  const contextValue = React.useMemo(() => {
    return {
      ...paramsValue,
      checkUrlParam: checkUrlParamSaveContext,
      checkUrlParamSaveContext
    }
  }, [paramsValue, checkUrlParamSaveContext])

  // pass resolved params through context value
  return React.createElement(
    AppParamsContext.Provider,
    {
      value: contextValue
    },
    props.children
  )
}
export const AppParamsThemeSync = () => {
  const { themeName } = React.useContext(AppParamsContext) || {}
  React.useEffect(() => {
    try {
      if (!themeName) return
      const key = addKeyLocalStorage('dataTheme')
      const existing = JSON.parse(localStorage.getItem(key) || '{}') || {}
      // set stationsLogo to public/<THEME>/logo.png by convention
      const candidateLogo = `/${themeName}/logo.png`
      existing.stationsLogo = candidateLogo
      localStorage.setItem(key, JSON.stringify(existing))
      document.body.setAttribute('data-theme', themeName)
    } catch (e) {
      // ignore
    }
  }, [themeName])
  return null
}
export const useAppParamsContext = () => {
  const context = React.useContext(AppParamsContext)
  if (!context) {
    throw new Error('useAppParamsContext must be used within AppParamsContextProvider')
  }
  return context
}
